import { ClearRounded, FileDownloadRounded, RefreshRounded, UploadRounded } from "@mui/icons-material";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Typography,
} from "@mui/material";
import axios from "axios";
import ExcelJS from "exceljs";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";
import { useMemo, useRef, useState } from "react";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { BUY_QTY_SHEET_API } from "../data";

const ID_KEY_OPTIONS = {
	option_id: { key: "option_id", name: "option_id", headers: ["option_id"] },
	style_ref_no: { key: "style_ref_no", name: "style_ref_no", headers: ["style_ref_no"] },
};

const BUY_LEVEL_OPTIONS = {
	CHANNEL: { key: "CHANNEL", name: "Channel Level" },
	REGION: { key: "REGION", name: "Region Level" },
};

const QTY_COLUMNS = ["channel", "region", "warehouse", "size", "total_qty"];

const normalizeHeader = (value) =>
	String(value ?? "")
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "");

const getDefaultIndex = (list, targetKeys) => list.findIndex((header) => targetKeys.includes(normalizeHeader(header)));

const multipartHeaders = {
	headers: { "Content-Type": "multipart/form-data" },
	transformRequest: [
		(data, headers) => {
			if (typeof FormData !== "undefined" && data instanceof FormData) {
				delete headers["Content-Type"];
				delete headers["content-type"];
			}
			return data;
		},
	],
};

const UploadModal = ({ modal, setModal, callbackFun }) => {
	const fileRef = useRef(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [fileName, setFileName] = useState("");
	const [headers, setHeaders] = useState([]);
	const [sampleRows, setSampleRows] = useState([]);
	const [totalRows, setTotalRows] = useState(0);
	const [previewLimit, setPreviewLimit] = useState(1000);
	const [idKeyType, setIdKeyType] = useState(ID_KEY_OPTIONS.option_id.key);
	const [buyLevel, setBuyLevel] = useState(BUY_LEVEL_OPTIONS.CHANNEL.key);
	const [idColumnIndex, setIdColumnIndex] = useState("");
	const [columnMap, setColumnMap] = useState({});
	const [publish, setPublish] = useState(false);
	const [previewRows, setPreviewRows] = useState([]);
	const [lastUploadResult, setLastUploadResult] = useState(null);

	const idKeyLabel = ID_KEY_OPTIONS[idKeyType]?.name || "option_id";
	const columnsMapped =
		idColumnIndex !== "" && QTY_COLUMNS.every((key) => columnMap[key] !== "" && columnMap[key] != null);

	const resetState = () => {
		fileRef.current = null;
		setLoading(false);
		setError("");
		setFileName("");
		setHeaders([]);
		setSampleRows([]);
		setTotalRows(0);
		setPreviewLimit(1000);
		setIdColumnIndex("");
		setColumnMap({});
		setPublish(false);
		setPreviewRows([]);
		setLastUploadResult(null);
	};

	const handleClose = () => {
		resetState();
		setIdKeyType(ID_KEY_OPTIONS.option_id.key);
		setBuyLevel(BUY_LEVEL_OPTIONS.CHANNEL.key);
		setModal(false);
	};

	const detectColumns = (headerList, keyType) => {
		const idIndex = getDefaultIndex(headerList, ID_KEY_OPTIONS[keyType]?.headers || ["option_id"]);
		const nextMap = {};
		QTY_COLUMNS.forEach((key) => {
			const index = getDefaultIndex(headerList, [key]);
			nextMap[key] = index >= 0 ? String(index) : "";
		});
		return {
			idIndex: idIndex >= 0 ? String(idIndex) : "",
			columnMap: nextMap,
		};
	};

	const buildPreviewRows = (rows, idColumn, map, keyType) => {
		if (idColumn === "" || QTY_COLUMNS.some((key) => map[key] === "" || map[key] == null)) return [];

		return rows.map((row, index) => ({
			__rowId: `buy-qty-preview-${index}`,
			[ID_KEY_OPTIONS[keyType]?.name || "option_id"]: row?.[Number(idColumn)] || "",
			channel: row?.[Number(map.channel)] || "",
			region: row?.[Number(map.region)] || "",
			warehouse: row?.[Number(map.warehouse)] || "",
			size: row?.[Number(map.size)] || "",
			total_qty: row?.[Number(map.total_qty)] || "",
			__status: "Preview",
			__note: "Validation runs on Save",
		}));
	};

	const handleFileChange = async (event) => {
		const selectedFile = event.target.files?.[0];
		event.target.value = "";
		if (!selectedFile) return;

		setLoading(true);
		setError("");
		setLastUploadResult(null);

		try {
			const formData = new FormData();
			formData.append("file", selectedFile);
			const { data } = await axios.post(`${BUY_QTY_SHEET_API}/bulk_upload_buy_qty_preview`, formData, multipartHeaders);
			const result = data?.result || {};
			const headerList = result.headers || [];
			const detected = detectColumns(headerList, idKeyType);

			fileRef.current = selectedFile;
			setFileName(selectedFile.name);
			setHeaders(headerList);
			setSampleRows(result.sample_rows || []);
			setTotalRows(result.total_rows || 0);
			setPreviewLimit(result.preview_limit || 1000);
			setIdColumnIndex(detected.idIndex);
			setColumnMap(detected.columnMap);
			setPreviewRows(buildPreviewRows(result.sample_rows || [], detected.idIndex, detected.columnMap, idKeyType));
		} catch (uploadError) {
			resetState();
			setError(parseErrorMessage(uploadError) || "Unable to preview the selected file.");
		} finally {
			setLoading(false);
		}
	};

	const handleIdKeyTypeChange = (nextKeyType) => {
		setIdKeyType(nextKeyType);
		setLastUploadResult(null);
		if (!headers.length) {
			setIdColumnIndex("");
			setPreviewRows([]);
			return;
		}
		const detected = detectColumns(headers, nextKeyType);
		setIdColumnIndex(detected.idIndex);
		setPreviewRows(buildPreviewRows(sampleRows, detected.idIndex, columnMap, nextKeyType));
	};

	const handlePreview = () => {
		setLastUploadResult(null);
		setPreviewRows(buildPreviewRows(sampleRows, idColumnIndex, columnMap, idKeyType));
	};

	const handleDownloadFormat = async () => {
		const workbook = new ExcelJS.Workbook();
		const worksheet = workbook.addWorksheet("Buy Qty Upload");
		const sampleId = idKeyType === "style_ref_no" ? "REF_001" : "RR-30000-OPT-10291";
		const sampleRegion = buyLevel === BUY_LEVEL_OPTIONS.CHANNEL.key ? "ALL" : "NORTH";
		worksheet.addRow(["color_buy_id", "option_id", "style_ref_no", ...QTY_COLUMNS]);
		worksheet.addRow([
			"",
			idKeyType === "option_id" ? sampleId : "RR-30000-OPT-10291",
			idKeyType === "style_ref_no" ? sampleId : "REF_001",
			"EBO",
			sampleRegion,
			"GGN",
			"M",
			10,
		]);

		const buffer = await workbook.xlsx.writeBuffer();
		const blob = new Blob([buffer], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		});
		const link = document.createElement("a");
		link.href = URL.createObjectURL(blob);
		link.download = `buy_qty_sheet_upload_format_${idKeyLabel}_${buyLevel.toLowerCase()}.xlsx`;
		link.click();
		URL.revokeObjectURL(link.href);
	};

	const handleFormSubmit = async (event) => {
		event.preventDefault();
		if (!fileRef.current || !columnsMapped) return;

		setLoading(true);
		setError("");
		try {
			const formData = new FormData();
			formData.append("file", fileRef.current);
			formData.append("id_type", idKeyType);
			formData.append("buy_level", buyLevel);
			formData.append("publish", String(!!publish));
			formData.append(
				"column_map",
				JSON.stringify({
					id: idColumnIndex,
					...columnMap,
				}),
			);

			const { data } = await axios.post(`${BUY_QTY_SHEET_API}/bulk_upload_buy_qty`, formData, multipartHeaders);
			const result = data?.result || {};
			setLastUploadResult(result);
			setPreviewRows(
				(result.preview_rows || []).map((row, index) => ({
					__rowId: row.__rowId || `buy-qty-preview-${index}`,
					[idKeyLabel]: row.identifier || "",
					channel: row.channel || "",
					region: row.region || "",
					warehouse: row.warehouse || "",
					size: row.size || "",
					total_qty: row.total_qty ?? "",
					option_id: row.option_id || "",
					__status: row.status || "Invalid",
					__note: row.note || "",
					__isValid: row.status === "Uploaded",
				})),
			);
			if (typeof callbackFun === "function") await callbackFun(data);
			if ((result.error_rows || 0) > 0 || (result.failed || 0) > 0) {
				setError(data?.message || "Some rows could not be uploaded.");
			}
		} catch (uploadError) {
			const message = parseErrorMessage(uploadError) || "Upload failed.";
			setError(message);
			if (typeof callbackFun === "function") await callbackFun(uploadError?.response?.data || false);
		} finally {
			setLoading(false);
		}
	};

	const handleDownloadStatus = async () => {
		const statusFileId = lastUploadResult?.status_file_id;
		if (!statusFileId) return;

		setLoading(true);
		try {
			const response = await axios.get(`${BUY_QTY_SHEET_API}/bulk_upload_buy_qty_status/${statusFileId}`, {
				responseType: "blob",
			});
			const blob = new Blob([response.data], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			});
			const link = document.createElement("a");
			link.href = URL.createObjectURL(blob);
			link.download = `buy_qty_upload_status_${idKeyLabel}_${buyLevel.toLowerCase()}.xlsx`;
			link.click();
			URL.revokeObjectURL(link.href);
		} catch (downloadError) {
			setError(parseErrorMessage(downloadError) || "Unable to download upload status.");
		} finally {
			setLoading(false);
		}
	};

	const validCount = lastUploadResult?.uploaded_rows ?? null;
	const errorCount = lastUploadResult?.error_rows ?? null;
	const uploadPreviewColumns = useMemo(
		() => [
			{
				key: "__upload_status",
				name: "STATUS",
				headerAlign: "center",
				align: "center",
				minWidth: 110,
				renderCell: ({ row }) => (
					<Box color={row.__isValid === false ? "error.main" : "success.main"} fontWeight={600}>
						{row.__status || "Preview"}
					</Box>
				),
			},
			{
				key: idKeyLabel,
				name: idKeyLabel.toUpperCase(),
				headerAlign: "center",
				align: "center",
				minWidth: 160,
			},
			...QTY_COLUMNS.map((key) => ({
				key,
				name: key.toUpperCase(),
				headerAlign: "center",
				align: "center",
				minWidth: 110,
			})),
			{
				key: "__note",
				name: "NOTE",
				headerAlign: "center",
				align: "center",
				minWidth: 260,
			},
		],
		[idKeyLabel],
	);

	return (
		<Dialog maxWidth="xl" fullWidth scroll="paper" open={modal} onClose={handleClose} PaperProps={{ sx: { height: "82vh" } }}>
			<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
				<Typography variant="h6">Upload Buy Qty</Typography>
				<IconButton onClick={handleClose}>
					<ClearRounded />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden", pt: 1.5, pb: 1.5 }}>
				<Stack spacing={1.25} sx={{ minHeight: 0, flex: 1, overflow: "hidden" }}>
					<Alert severity="warning" sx={{ py: 0.5, alignItems: "flex-start" }}>
						Upload the <strong>complete qty</strong> for every color in your Excel. Relook will delete that color’s
						existing qty and replace it with the Excel data. Colors not in the file will stay as they are.
					</Alert>
					{previewRows.length === 0 && (
						<Alert severity="info" sx={{ py: 0.5, alignItems: "center" }}>
							Choose upload key (<strong>option_id</strong> or <strong>style_ref_no</strong>) and buy level. The file is
							parsed on the server. Preview shows at most {previewLimit} rows. Save validates the full file.
						</Alert>
					)}

					<Box>
						<Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
							<FormControl size="small" sx={{ minWidth: 180 }}>
								<InputLabel id="upload-buy-qty-id-type-label">Upload By</InputLabel>
								<Select
									labelId="upload-buy-qty-id-type-label"
									label="Upload By"
									value={idKeyType}
									onChange={(event) => handleIdKeyTypeChange(event.target.value)}
									disabled={loading}>
									<MenuItem value={ID_KEY_OPTIONS.option_id.key}>option_id</MenuItem>
									<MenuItem value={ID_KEY_OPTIONS.style_ref_no.key}>style_ref_no</MenuItem>
								</Select>
							</FormControl>
							<FormControl size="small" sx={{ minWidth: 180 }}>
								<InputLabel id="upload-buy-qty-level-label">Buy Level</InputLabel>
								<Select
									labelId="upload-buy-qty-level-label"
									label="Buy Level"
									value={buyLevel}
									onChange={(event) => setBuyLevel(event.target.value)}
									disabled={loading}>
									<MenuItem value={BUY_LEVEL_OPTIONS.CHANNEL.key}>Channel Level</MenuItem>
									<MenuItem value={BUY_LEVEL_OPTIONS.REGION.key}>Region Level</MenuItem>
								</Select>
							</FormControl>
							<Button
								component="label"
								variant="contained"
								size="small"
								sx={{ minWidth: 150, textTransform: "none", fontWeight: 600, borderRadius: 2, py: 0.9 }}
								startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <UploadRounded />}>
								Choose CSV / Excel
								<input hidden type="file" accept=".csv,.xlsx" onChange={handleFileChange} disabled={loading} />
							</Button>
							<Button
								variant="outlined"
								color="secondary"
								onClick={handleDownloadFormat}
								disabled={loading}
								size="small"
								startIcon={<FileDownloadRounded />}
								sx={{ minWidth: 138, textTransform: "none", fontWeight: 600, borderRadius: 2, borderStyle: "dashed", py: 0.9 }}>
								Upload Format
							</Button>
							<Button
								variant="contained"
								color="success"
								size="small"
								startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshRounded />}
								onClick={handlePreview}
								disabled={loading || !headers.length || !columnsMapped}
								sx={{ minWidth: 132, textTransform: "none", fontWeight: 600, borderRadius: 2, py: 0.9 }}>
								Preview Rows
							</Button>
						</Stack>
						{fileName && (
							<Typography variant="body2" color="text.secondary" pt={1}>
								{`${fileName} - ${totalRows} rows found`}
							</Typography>
						)}
					</Box>

					{headers.length > 0 && (
						<Box display="flex" flexWrap="wrap" gap={2} pt={1}>
							<FormControl size="small" sx={{ minWidth: 220 }}>
								<InputLabel id="upload-buy-qty-id-column-label">{idKeyLabel} Column</InputLabel>
								<Select
									labelId="upload-buy-qty-id-column-label"
									label={`${idKeyLabel} Column`}
									value={idColumnIndex}
									onChange={(event) => setIdColumnIndex(event.target.value)}>
									{headers.map((header, index) => (
										<MenuItem key={`${header}-${index}`} value={String(index)}>
											{header}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							{QTY_COLUMNS.map((key) => (
								<FormControl key={key} size="small" sx={{ minWidth: 180 }}>
									<InputLabel id={`upload-buy-qty-${key}-label`}>{key}</InputLabel>
									<Select
										labelId={`upload-buy-qty-${key}-label`}
										label={key}
										value={columnMap[key] ?? ""}
										onChange={(event) => setColumnMap((prev) => ({ ...prev, [key]: event.target.value }))}>
										<MenuItem value="">Not mapped</MenuItem>
										{headers.map((header, index) => (
											<MenuItem key={`${header}-${index}`} value={String(index)}>
												{header}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							))}
						</Box>
					)}

					{error && <Alert severity="error">{error}</Alert>}

					<Stack sx={{ minHeight: 0, flex: 1, overflow: "hidden" }}>
						{previewRows.length > 0 && (
							<Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
								<TanstackDatagrid
									columns={uploadPreviewColumns}
									pageData={previewRows}
									setPageData={setPreviewRows}
									getRowId={(row) => row.__rowId}
									disablePagination
									serverSidePagination={false}
									enableHorizontalScroll
									headerHeight={40}
									rowHeight={35}
									processRowUpdate={(newRow) => newRow}
									isCellEditable={() => false}
									children={
										<Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }} gap={2} flexWrap="wrap">
											<Typography variant="body2" color="text.secondary">
												{lastUploadResult
													? `Total ${lastUploadResult.total_rows ?? totalRows} row(s) · Valid ${validCount ?? 0} · Error ${errorCount ?? 0}`
													: `Total ${totalRows} row(s) · Showing first ${previewRows.length} of ${totalRows}`}
											</Typography>
										</Box>
									}
								/>
							</Box>
						)}
					</Stack>

					<FormControlLabel
						control={
							<Checkbox
								checked={publish}
								onChange={(event) => setPublish(event.target.checked)}
								disabled={loading || !fileRef.current}
							/>
						}
						label="Publish after upload"
					/>
				</Stack>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button variant="outlined" onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					variant="outlined"
					startIcon={<FileDownloadRounded />}
					onClick={handleDownloadStatus}
					disabled={loading || !lastUploadResult?.status_file_id}>
					Download Status
				</Button>
				<Button variant="contained" onClick={handleFormSubmit} disabled={loading || !fileRef.current || !columnsMapped || !totalRows}>
					Save Upload
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default UploadModal;
